import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { getExamStats } from '../../../api/content';

const KeyStats = ({ theme }) => {
  const [examStats, setExamStats] = useState([]);
  const [animatedNumbers, setAnimatedNumbers] = useState([]);
  const [statTypes, setStatTypes] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Get exam stats
    const stats = getExamStats();
    setExamStats(stats);

    // Calculate totals and round to nearest 50
    const totalQuestions = Math.round(stats.reduce((sum, exam) => sum + exam.questions, 0) / 50) * 50;
    const totalArticles = Math.round(stats.reduce((sum, exam) => sum + exam.articles, 0) / 50) * 50;
    const totalFlashcards = Math.round(stats.reduce((sum, exam) => sum + exam.flashcards, 0) / 50) * 50;
    const totalGlossary = Math.round(stats.reduce((sum, exam) => sum + exam.glossary, 0) / 50) * 50;
    const totalAcronyms = Math.round(stats.reduce((sum, exam) => sum + exam.acronyms, 0) / 50) * 50;

    // Only include stats with values > 0
    const totals = [];
    const types = [];

    if (totalQuestions > 0) {
      totals.push(totalQuestions);
      types.push('questions');
    }
    if (totalArticles > 0) {
      totals.push(totalArticles);
      types.push('articles');
    }
    if (totalFlashcards > 0) {
      totals.push(totalFlashcards);
      types.push('flashcards');
    }
    if (totalGlossary > 0) {
      totals.push(totalGlossary);
      types.push('glossary');
    }
    if (totalAcronyms > 0) {
      totals.push(totalAcronyms);
      types.push('acronyms');
    }

    // Set stat types for later use
    setStatTypes(types);

    // Initialize animated numbers
    setAnimatedNumbers(totals.map(() => 0));

    // Animate numbers
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    const animateNumbers = () => {
      let currentStep = 0;
      const interval = setInterval(() => {
        if (currentStep >= steps) {
          clearInterval(interval);
          return;
        }

        setAnimatedNumbers(prevNumbers =>
          prevNumbers.map((current, index) => {
            const target = totals[index];
            const increment = target / steps;
            return Math.min(Math.round(current + increment), target);
          })
        );

        currentStep++;
      }, stepDuration);
    };

    animateNumbers();
  }, []);

  const styles = {
    container: {
      paddingTop: '15px',
      paddingBottom: '15px',
      textAlign: 'center',
      scrollMarginTop: '80px',
      position: 'relative'
    },
    innerContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 0.5rem'
    },
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
      gap: 'clamp(1rem, 3vw, 2rem)',
      textAlign: 'center'
    },
    statItem: {
      padding: 'clamp(1rem, 3vw, 1.5rem)',
      background: theme?.background?.card,
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      border: `1px solid ${theme?.borders?.primary || '#E5E7EB'}`
    },
    statNumber: {
      fontSize: 'clamp(1.75rem, 6vw, 2.5rem)',
      fontWeight: '800',
      color: theme?.primary?.blue?.[500],
      marginBottom: '0.25rem',
      display: 'block',
    },
    statLabel: {
      color: theme?.text?.primary,
      fontWeight: '600',
      fontSize: 'clamp(1.125rem, 3vw, 1.5rem)',
      display: 'block'
    },
    examBreakdown: {
      color: theme?.text?.secondary || '#666',
      fontSize: 'clamp(0.75rem, 2vw, 0.9rem)',
      fontWeight: '500',
      lineHeight: '1.4',
      marginTop: '0.5rem'
    },
    viewDetailsButton: {
      background: 'none',
      border: `1px solid ${theme?.primary?.blue?.[500] || '#0973ba'}`,
      borderRadius: '0.5rem',
      color: theme?.primary?.blue?.[500] || '#0973ba',
      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
      fontWeight: '600',
      cursor: 'pointer',
      padding: '0.75rem 1.5rem',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginTop: '1.5rem',
      transition: 'all 0.2s ease'
    },
    expandableSection: {
      overflow: 'hidden',
      transition: 'max-height 0.4s ease-in-out, opacity 0.3s ease-in-out',
    },
    tableContainer: {
      marginTop: '1.5rem',
      background: theme?.background?.card,
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      border: `1px solid ${theme?.borders?.primary || '#E5E7EB'}`,
      overflow: 'hidden'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      textAlign: 'left'
    },
    tableHeader: {
      background: theme?.background?.secondary || '#f8f9fa',
      borderBottom: `2px solid ${theme?.borders?.primary || '#E5E7EB'}`
    },
    tableHeaderCell: {
      padding: '1rem',
      fontWeight: '600',
      fontSize: 'clamp(0.8rem, 2vw, 0.95rem)',
      color: theme?.text?.primary,
      textAlign: 'center'
    },
    tableHeaderCellFirst: {
      textAlign: 'left',
      paddingLeft: '1.5rem'
    },
    tableRow: {
      borderBottom: `1px solid ${theme?.borders?.primary || '#E5E7EB'}`,
    },
    tableRowLast: {
      borderBottom: 'none'
    },
    tableCell: {
      padding: '0.875rem 1rem',
      fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
      color: theme?.text?.secondary || '#666',
      textAlign: 'center'
    },
    tableCellFirst: {
      fontWeight: '500',
      color: theme?.text?.primary,
      textAlign: 'left',
      paddingLeft: '1.5rem'
    },
    tableCellNumber: {
      fontWeight: '600',
      color: theme?.primary?.blue?.[500] || '#0973ba'
    }
  };

  const getExamBreakdown = (type) => {
    if (examStats.length === 0) return '';
    return examStats.map(exam => `${exam.title}: ${exam[type]}`).join(' | ');
  };

  const getStatLabel = (type) => {
    const labels = {
      questions: 'Study Questions',
      articles: 'Study Articles',
      flashcards: 'Flashcards',
      glossary: 'Glossary Terms',
      acronyms: 'Acronyms'
    };
    return labels[type] || type;
  };

  const getShortLabel = (type) => {
    const labels = {
      questions: 'Questions',
      articles: 'Articles',
      flashcards: 'Flashcards',
      glossary: 'Glossary',
      acronyms: 'Acronyms'
    };
    return labels[type] || type;
  };

  const statItems = statTypes.map((type, index) => ({
    type,
    number: animatedNumbers[index] || 0,
    label: getStatLabel(type),
    breakdown: getExamBreakdown(type)
  }));

  const showExpandable = examStats.length > 3;

  return (
    <section id="key-stats" style={styles.container}>
      <div style={styles.innerContainer}>
        {/* Stats Cards */}
        <div style={styles.statsContainer}>
          {statItems.map((stat, index) => (
            <div key={index} style={styles.statItem}>
              <span style={styles.statNumber}>
                {stat.number}+
              </span>
              <span style={styles.statLabel}>
                {stat.label}
              </span>
              {/* For 3 or fewer exams, show inline breakdown */}
              {!showExpandable && (
                <div style={styles.examBreakdown}>
                  {stat.breakdown}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Single View Details button for 4+ exams */}
        {showExpandable && (
          <>
            <button
              style={styles.viewDetailsButton}
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
            >
              <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} style={{ marginRight: '0.5rem' }} />
              {isExpanded ? 'Hide Exam Details' : 'View Exam Details'}
            </button>

            {/* Expandable Table */}
            <div
              style={{
                ...styles.expandableSection,
                maxHeight: isExpanded ? '800px' : '0',
                opacity: isExpanded ? 1 : 0,
              }}
            >
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead style={styles.tableHeader}>
                    <tr>
                      <th style={{ ...styles.tableHeaderCell, ...styles.tableHeaderCellFirst }}>
                        Exam
                      </th>
                      {statTypes.map((type) => (
                        <th key={type} style={styles.tableHeaderCell}>
                          {getShortLabel(type)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {examStats.map((exam, idx) => (
                      <tr
                        key={idx}
                        style={{
                          ...styles.tableRow,
                          ...(idx === examStats.length - 1 ? styles.tableRowLast : {})
                        }}
                      >
                        <td style={{ ...styles.tableCell, ...styles.tableCellFirst }}>
                          {exam.title}
                        </td>
                        {statTypes.map((type) => (
                          <td key={type} style={{ ...styles.tableCell, ...styles.tableCellNumber }}>
                            {exam[type]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default KeyStats;
